import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Flex, Tabs, Text } from "@radix-ui/themes";
import { Plus, Users, Ticket, Gift, Coins } from "lucide-react";
import PageTemplate from "@/components/page-template/PageTemplate";
import withPermissionRequired from "@/hoc/withPermissionRequired";
import Referrals from "./Referrals";
import PromoCodes from "./PromoCodes";
import EngagementTrial from "./EngagementTrial";
import Balances from "./Balances";

type GrowthTab = "referrals" | "promo-codes" | "balances" | "engagement";

function GrowthPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const activeTab: GrowthTab =
    tabParam === "promo-codes" || tabParam === "engagement" || tabParam === "balances"
      ? tabParam
      : "referrals";

  const setActiveTab = (tab: string) => {
    const next = new URLSearchParams(searchParams);
    if (tab === "promo-codes" || tab === "engagement" || tab === "balances") {
      next.set("tab", tab);
    } else {
      next.delete("tab");
    }
    setSearchParams(next, { replace: true });
  };

  const [referralsCreateOpen, setReferralsCreateOpen] = useState(false);
  const [promoCreateOpen, setPromoCreateOpen] = useState(false);

  const rightContent =
    activeTab === "referrals" ? (
      <Flex gap="2">
        <Button onClick={() => setReferralsCreateOpen(true)}>
          <Plus size={16} />
          New programme
        </Button>
      </Flex>
    ) : activeTab === "promo-codes" ? (
      <Flex gap="2">
        <Button onClick={() => setPromoCreateOpen(true)}>
          <Plus size={16} />
          New promo code
        </Button>
      </Flex>
    ) : null;

  return (
    <PageTemplate
      title="Growth"
      description="Run referral programmes, promo codes, and engagement offers to acquire and retain users."
      rightContent={rightContent}
    >
      <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
        <Tabs.List mb="4">
          <Tabs.Trigger value="referrals">
            <Users size={14} style={{ marginRight: 6 }} />
            <Text>Referrals</Text>
          </Tabs.Trigger>
          <Tabs.Trigger value="promo-codes">
            <Ticket size={14} style={{ marginRight: 6 }} />
            <Text>Promo codes</Text>
          </Tabs.Trigger>
          <Tabs.Trigger value="balances">
            <Coins size={14} style={{ marginRight: 6 }} />
            <Text>Balances</Text>
          </Tabs.Trigger>
          <Tabs.Trigger value="engagement">
            <Gift size={14} style={{ marginRight: 6 }} />
            <Text>Engagement</Text>
          </Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="referrals">
          <Referrals
            isCreateOpen={referralsCreateOpen}
            onCreateOpenChange={setReferralsCreateOpen}
          />
        </Tabs.Content>
        <Tabs.Content value="promo-codes">
          <PromoCodes
            isCreateOpen={promoCreateOpen}
            onCreateOpenChange={setPromoCreateOpen}
          />
        </Tabs.Content>
        <Tabs.Content value="balances">
          <Balances />
        </Tabs.Content>
        <Tabs.Content value="engagement">
          <EngagementTrial />
        </Tabs.Content>
      </Tabs.Root>
    </PageTemplate>
  );
}

export default withPermissionRequired(
  "anythink_subscription_plans:read",
  GrowthPage,
);
